import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { Button, Card, Spinner, Stack } from "@/components";
import { AdjudicacionesTab } from "@/tabs/adjudicaciones/AdjudicacionesTab";
import { AportesTab } from "@/tabs/aportes/AportesTab";
import { AvanceTab } from "@/tabs/avance/AvanceTab";
import { CacTab } from "@/tabs/cac/CacTab";
import { CajaTab } from "@/tabs/caja/CajaTab";
import { AvanceObraTab } from "@/tabs/configuracion/AvanceObraTab";
import { MensajesProveedoresTab } from "@/tabs/configuracion/MensajesProveedoresTab";
import { AvisosTab } from "@/tabs/configuracion/AvisosTab";
import { ConfigFideicomisoTab } from "@/tabs/configuracion/ConfigFideicomisoTab";
import { PonderacionPorDefectoTab } from "@/tabs/configuracion/PonderacionPorDefectoTab";
import { RubrosTab } from "@/tabs/configuracion/RubrosTab";
import { UsuariosTab } from "@/tabs/configuracion/UsuariosTab";
import { CotizacionesTab } from "@/tabs/cotizaciones/CotizacionesTab";
import { CotizacionTab } from "@/tabs/cotizaciones/CotizacionTab";
import { CuentaTab } from "@/tabs/cuentas/CuentaTab";
import { CuentasCorrientesTab } from "@/tabs/cuentas/CuentasCorrientesTab";
import { FiduciantesTab } from "@/tabs/fiduciantes/FiduciantesTab";
import { ProyectoTab } from "@/tabs/proyecto/ProyectoTab";
import { RecorridoTab } from "@/tabs/recorrido/RecorridoTab";
import { GastoTab } from "@/tabs/gastos/GastoTab";
import { GastosTab } from "@/tabs/gastos/GastosTab";
import { InicioTab } from "@/tabs/inicio/InicioTab";
import { InvitacionTab } from "@/tabs/invitacion/InvitacionTab";
import { GananciaTab } from "@/tabs/ganancia/GananciaTab";
import { HonorariosTab } from "@/tabs/honorarios/HonorariosTab";
import { LlamadoTab } from "@/tabs/llamados/LlamadoTab";
import { LlamadosTab } from "@/tabs/llamados/LlamadosTab";
import { PedidoTab } from "@/tabs/pedido/PedidoTab";
import { HistorialTab } from "@/tabs/historial/HistorialTab";
import { LoginTab } from "@/tabs/login/LoginTab";
import { MiCuentaTab } from "@/tabs/mi-cuenta/MiCuentaTab";
import { ParticipacionTab } from "@/tabs/participacion/ParticipacionTab";
import { GruposTab } from "@/tabs/grupos/GruposTab";
import { PerfilTab } from "@/tabs/perfil/PerfilTab";
import { PrimerIngresoTab } from "@/tabs/primer-ingreso/PrimerIngresoTab";
import { CronogramaTab } from "@/tabs/cronograma/CronogramaTab";
import { CashFlowTab } from "@/tabs/cash-flow/CashFlowTab";
import { PresupuestoRubroTab } from "@/tabs/presupuesto/PresupuestoRubroTab";
import { PresupuestoTab } from "@/tabs/presupuesto/PresupuestoTab";
import { ProveedoresTab } from "@/tabs/proveedores/ProveedoresTab";
import { ReportesTab } from "@/tabs/reportes/ReportesTab";
import { TerrenoTab } from "@/tabs/terreno/TerrenoTab";
import { CompraTerrenoTab } from "@/tabs/gastos/CompraTerrenoTab";
import { UnidadTab } from "@/tabs/unidades/UnidadTab";
import { UnidadesTab } from "@/tabs/unidades/UnidadesTab";
import { PonderacionTab } from "@/tabs/ponderacion/PonderacionTab";
import { CobrosTab } from "@/tabs/ventas/CobrosTab";
import { CompradoresTab } from "@/tabs/ventas/CompradoresTab";
import { VentasTab } from "@/tabs/ventas/VentasTab";
import { VentaTab } from "@/tabs/ventas/VentaTab";
import { ConRol, Privado, SoloAdmin } from "./Privado";
import { useSesion } from "./useSesion";
import { rolesDe } from "@/lib/navegacion";

export function App() {
  return (
    <BrowserRouter>
      <Rutas />
    </BrowserRouter>
  );
}

function Rutas() {
  const { estado, alIniciar, cerrarSesion, reintentar } = useSesion();
  const location = useLocation();

  if (estado.tipo === "cargando") {
    return (
      <div className="grid min-h-dvh place-items-center text-primario">
        <Spinner />
      </div>
    );
  }
  if (estado.tipo === "error") {
    return (
      <div className="grid min-h-dvh place-items-center p-4">
        <Card className="max-w-sm">
          <Stack espacio={3}>
            <p>{estado.mensaje}</p>
            <Button onClick={() => void reintentar()}>Reintentar</Button>
          </Stack>
        </Card>
      </div>
    );
  }

  const usuario = estado.tipo === "logueado" ? estado.usuario : null;
  const destino = (location.state as { desde?: string } | null)?.desde ?? "/";

  // Entró con la contraseña temporal: nada más es navegable hasta que elija la suya (el backend igual responde 403).
  if (usuario?.debe_cambiar_password) {
    return <PrimerIngresoTab usuario={usuario} onListo={reintentar} onSalir={() => void cerrarSesion()} />;
  }

  return (
    <Routes>
      <Route path="/login" element={usuario ? <Navigate to={destino} replace /> : <LoginTab onIngreso={alIniciar} />} />
      <Route path="/invitacion/:token" element={<InvitacionTab onIngreso={alIniciar} />} />
      <Route
        element={
          usuario ? (
            <Privado usuario={usuario} onSalir={() => void cerrarSesion()} onRecargarUsuario={reintentar} />
          ) : (
            <Navigate to="/login" replace state={{ desde: location.pathname }} />
          )
        }
      >
        <Route index element={<InicioTab />} />
        <Route path="avance" element={<ConRol roles={rolesDe("/avance")}><AvanceTab /></ConRol>} />
        <Route path="perfil" element={<PerfilTab />} />
        <Route path="fiduciantes" element={<ConRol roles={rolesDe("/fiduciantes")}><FiduciantesTab /></ConRol>} />
        <Route path="proyecto" element={<ConRol roles={rolesDe("/proyecto")}><ProyectoTab /></ConRol>} />
        <Route path="proyecto/recorrido/:id" element={<ConRol roles={rolesDe("/proyecto")}><RecorridoTab /></ConRol>} />
        <Route path="unidades" element={<ConRol roles={rolesDe("/unidades")}><UnidadesTab /></ConRol>} />
        <Route path="unidades/:id" element={<ConRol roles={rolesDe("/unidades")}><UnidadTab /></ConRol>} />
        <Route path="ponderacion" element={<SoloAdmin><PonderacionTab /></SoloAdmin>} />
        <Route path="proveedores" element={<ConRol roles={rolesDe("/proveedores")}><ProveedoresTab /></ConRol>} />
        <Route path="cotizaciones" element={<ConRol roles={rolesDe("/cotizaciones")}><CotizacionesTab /></ConRol>} />
        <Route path="cotizaciones/:id" element={<ConRol roles={rolesDe("/cotizaciones")}><CotizacionTab /></ConRol>} />
        <Route path="gastos" element={<ConRol roles={rolesDe("/gastos")}><GastosTab /></ConRol>} />
        <Route path="gastos/compra-terreno" element={<ConRol roles={rolesDe("/gastos")}><CompraTerrenoTab /></ConRol>} />
        <Route path="gastos/:id" element={<ConRol roles={rolesDe("/gastos")}><GastoTab /></ConRol>} />
        <Route path="terreno" element={<ConRol roles={rolesDe("/terreno")}><TerrenoTab /></ConRol>} />
        <Route path="llamados" element={<ConRol roles={rolesDe("/llamados")} redirigir={{ fiduciante: "/mi-cuenta" }}><LlamadosTab /></ConRol>} />
        <Route path="llamados/:id" element={<ConRol roles={rolesDe("/llamados")} redirigir={{ fiduciante: "/mi-cuenta" }}><LlamadoTab /></ConRol>} />
        <Route path="historial" element={<ConRol roles={rolesDe("/historial")}><HistorialTab /></ConRol>} />
        <Route path="pedido" element={<ConRol roles={rolesDe("/pedido")}><PedidoTab /></ConRol>} />
        <Route path="mi-cuenta" element={<ConRol roles={rolesDe("/mi-cuenta")}><MiCuentaTab /></ConRol>} />
        <Route path="honorarios" element={<SoloAdmin><HonorariosTab /></SoloAdmin>} />
        <Route path="ganancia" element={<ConRol roles={rolesDe("/ganancia")}><GananciaTab /></ConRol>} />
        <Route path="aportes" element={<ConRol roles={rolesDe("/aportes")} redirigir={{ fiduciante: "/mi-cuenta" }}><AportesTab /></ConRol>} />
        <Route path="adjudicaciones" element={<ConRol roles={rolesDe("/adjudicaciones")}><AdjudicacionesTab /></ConRol>} />
        <Route path="cuentas-corrientes" element={<ConRol roles={rolesDe("/cuentas-corrientes")} redirigir={{ fiduciante: "/mi-cuenta" }}><CuentasCorrientesTab /></ConRol>} />
        <Route path="cuentas-corrientes/:id" element={<ConRol roles={rolesDe("/cuentas-corrientes")} redirigir={{ fiduciante: "/mi-cuenta" }}><CuentaTab /></ConRol>} />
        <Route path="grupos" element={<ConRol roles={rolesDe("/grupos")}><GruposTab /></ConRol>} />
        <Route path="participacion" element={<ConRol roles={rolesDe("/participacion")} redirigir={{ fiduciante: "/mi-cuenta" }}><ParticipacionTab /></ConRol>} />
        <Route path="ventas" element={<ConRol roles={rolesDe("/ventas")}><VentasTab /></ConRol>} />
        <Route path="ventas/:id" element={<ConRol roles={rolesDe("/ventas")}><VentaTab /></ConRol>} />
        <Route path="compradores" element={<ConRol roles={rolesDe("/compradores")}><CompradoresTab /></ConRol>} />
        <Route path="cobros" element={<ConRol roles={rolesDe("/cobros")}><CobrosTab /></ConRol>} />
        <Route path="cac" element={<ConRol roles={rolesDe("/cac")}><CacTab /></ConRol>} />
        <Route path="configuracion/cac" element={<Navigate to="/cac" replace />} />
        <Route path="caja" element={<ConRol roles={rolesDe("/caja")}><CajaTab /></ConRol>} />
        <Route path="presupuesto" element={<ConRol roles={rolesDe("/presupuesto")}><PresupuestoTab /></ConRol>} />
        <Route path="presupuesto/rubros/:id" element={<ConRol roles={rolesDe("/presupuesto")}><PresupuestoRubroTab /></ConRol>} />
        <Route path="cronograma" element={<ConRol roles={rolesDe("/cronograma")}><CronogramaTab /></ConRol>} />
        <Route path="cash-flow" element={<ConRol roles={rolesDe("/cash-flow")}><CashFlowTab /></ConRol>} />
        <Route path="reportes" element={<ConRol roles={rolesDe("/reportes")}><ReportesTab /></ConRol>} />
        <Route path="configuracion/fideicomiso" element={<SoloAdmin><ConfigFideicomisoTab /></SoloAdmin>} />
        <Route path="configuracion/rubros" element={<SoloAdmin><RubrosTab /></SoloAdmin>} />
        <Route path="configuracion/ponderacion" element={<SoloAdmin><PonderacionPorDefectoTab /></SoloAdmin>} />
        <Route path="configuracion/avisos" element={<SoloAdmin><AvisosTab /></SoloAdmin>} />
        <Route path="configuracion/avance" element={<SoloAdmin><AvanceObraTab /></SoloAdmin>} />
        <Route path="configuracion/mensajes" element={<SoloAdmin><MensajesProveedoresTab /></SoloAdmin>} />
        <Route path="configuracion/usuarios" element={<SoloAdmin><UsuariosTab /></SoloAdmin>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
